import React from "react";
import { MessageSquareWarning } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext.jsx";
import { useLanguage } from "@/contexts/LanguageContext.jsx";

function resolveFeedbackRole(auth = {}, roleOverride = "") {
  const explicit = String(roleOverride || "").trim().toLowerCase();
  if (explicit) return explicit;

  const role = String(
    auth?.profile?.role ||
      auth?.role ||
      auth?.user?.user_metadata?.primary_role ||
      auth?.user?.user_metadata?.role ||
      auth?.user?.app_metadata?.role ||
      "",
  )
    .trim()
    .toLowerCase();

  return role === "doctor" || role === "patient" || role === "admin" || role === "pharmacy"
    ? role
    : "";
}

function buildDeviceContext() {
  const width = typeof window !== "undefined" ? Number(window.innerWidth || 0) : 0;
  const height = typeof window !== "undefined" ? Number(window.innerHeight || 0) : 0;
  const deviceKind = width >= 1024 ? "desktop" : width >= 640 ? "tablet" : "mobile";

  return {
    kind: deviceKind,
    platform: typeof navigator !== "undefined" ? String(navigator.platform || "") : "",
    userAgent: typeof navigator !== "undefined" ? String(navigator.userAgent || "") : "",
    language: typeof navigator !== "undefined" ? String(navigator.language || "") : "",
    viewportWidth: width,
    viewportHeight: height,
    touchPoints:
      typeof navigator !== "undefined" ? Number(navigator.maxTouchPoints || 0) : 0,
  };
}

function mergeFeedbackContext(base = {}, additions = {}) {
  return {
    ...(base && typeof base === "object" ? base : {}),
    ...(additions && typeof additions === "object" ? additions : {}),
  };
}

function buildFeedbackUrl({ route = "", role = "", section = "", entity = null, context = {} } = {}) {
  const params = new URLSearchParams();
  const cleanRoute = String(route || "").trim();
  const cleanRole = String(role || "").trim();
  const cleanSection = String(section || "").trim();
  if (cleanRoute) {
    params.set("from", cleanRoute);
    params.set("returnTo", cleanRoute);
  }
  if (cleanRole) params.set("role", cleanRole);
  if (cleanSection) params.set("section", cleanSection);

  if (entity && typeof entity === "object") {
    const entityType = String(entity.type || "").trim();
    const entityId = String(entity.id || entity.label || entity.name || "").trim();
    if (entityType) params.set("entityType", entityType);
    if (entityId) params.set("entityId", entityId);
  }

  const mergedContext = mergeFeedbackContext(context, {
    entity: entity && typeof entity === "object" ? entity : null,
    device: buildDeviceContext(),
  });
  try {
    const json = JSON.stringify(mergedContext);
    if (json && json !== "{}") params.set("context", json);
  } catch {
    // keep route lightweight if context is not serializable
  }

  return `/feedback${params.toString() ? `?${params.toString()}` : ""}`;
}

export default function ContextFeedbackLauncher({
  section = "",
  context = {},
  entity = null,
  roleOverride = "",
  title = "",
  subtitle = "",
  successMessage = "",
  renderTrigger,
  triggerLabel = "",
  triggerClassName = "",
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
}) {
  const auth = useAuth() || {};
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const userId = String(auth?.user?.id || "").trim();
  const role = resolveFeedbackRole(auth, roleOverride);
  const route = `${location.pathname}${location.search || ""}${location.hash || ""}`;
  const disabled = !userId || !role;
  const feedbackUrl = React.useMemo(
    () => buildFeedbackUrl({ route, role, section, entity, context }),
    [context, entity, role, route, section]
  );
  const openFeedbackPage = React.useCallback(() => {
    if (disabled) return;
    navigate(feedbackUrl);
  }, [disabled, feedbackUrl, navigate]);

  React.useEffect(() => {
    if (controlledOpen !== true) return;
    if (typeof onOpenChange === "function") onOpenChange(false);
    openFeedbackPage();
  }, [controlledOpen, onOpenChange, openFeedbackPage]);

  const trigger =
    hideTrigger
      ? null
      :
    typeof renderTrigger === "function"
      ? renderTrigger({
          openDialog: openFeedbackPage,
          disabled,
        })
      : (
          <button
            type="button"
            disabled={disabled}
            onClick={openFeedbackPage}
            className={[
              "inline-flex items-center gap-2 rounded-full border border-[rgba(var(--clr-outline-variant),0.8)] bg-[rgb(var(--clr-surface-cont-highest))] px-3 py-1.5 text-label-medium text-[rgb(var(--clr-on-surface-variant))] transition hover:text-[rgb(var(--clr-on-surface))] disabled:cursor-not-allowed disabled:opacity-50",
              triggerClassName,
            ].join(" ")}
          >
            <MessageSquareWarning className="h-4 w-4" aria-hidden />
            <span>{triggerLabel || t("feedback.trigger", "Make suggestion")}</span>
          </button>
        );

  return (
    <>
      {trigger}
    </>
  );
}
